import { cn } from "@/shared/lib/cn";

/** Filtre « Consommation » des écrans de gestion des assurés : part atteinte du plafond. */
export const CONSUMPTION_OPTIONS = [
  { value: "50", label: "50 % et plus" },
  { value: "70", label: "70 % et plus" },
  { value: "90", label: "90 % et plus" },
];

export function reachesConsumption(percent: number | null, threshold: string): boolean {
  return !threshold || (percent !== null && percent >= Number(threshold));
}

export function formatAmount(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function consumptionText(consumed: number, ceiling: number | null, percent: number | null): string {
  if (ceiling === null) return `${formatAmount(consumed)} $ (sans plafond)`;
  return `${formatAmount(consumed)} / ${formatAmount(ceiling)} $ (${(percent ?? 0).toLocaleString("fr-FR")} %)`;
}

function tone(percent: number): string {
  if (percent >= 90) return "bg-red-500";
  if (percent >= 70) return "bg-amber-500";
  return "bg-emerald-500";
}

/** Consommé par rapport au plafond : barre, pourcentage et montants. */
export function ConsumptionBar({
  consumed,
  ceiling,
  percent,
  className,
}: {
  consumed: number;
  ceiling: number | null;
  percent: number | null;
  className?: string;
}) {
  // Sans plafond connu, la barre reste affichée, vide : le pourcentage n'est pas calculable.
  const known = ceiling !== null && percent !== null;
  const width = known ? Math.min(100, percent) : 0;

  return (
    <div className={cn("min-w-[11rem]", className)}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-semibold tabular-nums text-foreground">{known ? `${percent.toLocaleString("fr-FR")} %` : "—"}</span>
        <span className="tabular-nums text-muted">
          {known ? `${formatAmount(consumed)} / ${formatAmount(ceiling)} $` : `${formatAmount(consumed)} $ · plafond non défini`}
        </span>
      </div>
      <div
        className="mt-1 h-2 overflow-hidden rounded-full bg-card-muted"
        role="progressbar"
        aria-valuenow={width}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Consommation du plafond"
      >
        {known ? <div className={cn("h-full min-w-1 rounded-full", tone(percent))} style={{ width: `${width}%` }} /> : null}
      </div>
    </div>
  );
}
